import { DataSimpleInvestorTct } from '@dealstack/shared/lib/generated';

/**
 * Find an investor whose full name ("First Last") appears anywhere in the file
 * name, delimited on both sides by a word boundary (any non-letter/non-digit
 * character, or the start/end of the string). This matches files like:
 *   - "Thomas Tyteca.pdf"
 *   - "Thomas Tyteca Adherence Agreement.pdf"
 *   - "Amanda Phillips_Darktrace K-1.pdf"
 *   - "Investment agreement - Thomas Tyteca.pdf"
 *   - "1. Thomas Tyteca - investment agreement.pdf"
 *
 * When multiple investors match, the longest full name wins (so
 * "Thomas Tyteca Junior" beats the nested "Thomas Tyteca"). When two DIFFERENT
 * investors tie at the longest length, the result is ambiguous and we return
 * undefined rather than silently mis-assign.
 */
const isNameBoundary = (char: string | undefined): boolean =>
	char === undefined || !/[\p{L}\p{N}]/u.test(char);

const findInvestorByName = (
	investorList: DataSimpleInvestorTct[],
	fileName: string
): DataSimpleInvestorTct | undefined => {
	const nameWithoutExtension = fileName.replace(/\.[^.]+$/, '').toLowerCase();

	const matches = investorList
		.map((investor) => {
			const fullName = `${investor.user.firstName} ${investor.user.lastName}`
				.trim()
				.toLowerCase();
			if (!fullName) {
				return undefined;
			}
			// Scan every occurrence — the first hit might fail the boundary
			// check (e.g. investor "Jon Johns" inside "Jon Johnson ...") while
			// a later, valid occurrence still exists in the same file name.
			let idx = nameWithoutExtension.indexOf(fullName);
			while (idx !== -1) {
				if (
					isNameBoundary(nameWithoutExtension[idx - 1]) &&
					isNameBoundary(nameWithoutExtension[idx + fullName.length])
				) {
					return { investor, fullName, start: idx, end: idx + fullName.length };
				}
				idx = nameWithoutExtension.indexOf(fullName, idx + 1);
			}
			return undefined;
		})
		.filter((match): match is NonNullable<typeof match> => match !== undefined)
		.sort((a, b) => b.fullName.length - a.fullName.length);

	if (matches.length === 0) {
		return undefined;
	}

	const best = matches[0];
	// Ambiguous: another match exists for a DIFFERENT investor whose region does
	// not overlap with the best match. (Overlapping regions are nested names
	// like "Thomas Tyteca" inside "Thomas Tyteca Junior" — longest-wins handles
	// those correctly.)
	const hasConflict = matches.some(
		(m) => m.investor !== best.investor && (m.end <= best.start || m.start >= best.end)
	);
	if (hasConflict) {
		return undefined;
	}
	return best.investor;
};

/**
 * Find an investor by matching the file name (without extension) against the investor's email
 */
const findInvestorByEmail = (
	investorList: DataSimpleInvestorTct[],
	fileName: string
): DataSimpleInvestorTct | undefined => {
	const nameWithoutExtension = fileName.replace(/\.[^.]+$/, '');
	return investorList.find(
		(investor) => investor.user.email.toLowerCase() === nameWithoutExtension.toLowerCase()
	);
};

/**
 * Find an investor by matching the file name (without extension) against the investor's user ID or investor ID
 */
const findInvestorById = (
	investorList: DataSimpleInvestorTct[],
	fileName: string
): DataSimpleInvestorTct | undefined => {
	const nameWithoutExtension = fileName.replace(/\.[^.]+$/, '');
	return investorList.find(
		(investor) =>
			investor.user.id === nameWithoutExtension || investor.id === nameWithoutExtension
	);
};

export { findInvestorByName, findInvestorByEmail, findInvestorById };
